(() => {
  const STORAGE_KEY = 'jsd-selected-vehicle';
  const EVENT_NAME = 'jsd:vehicle-change';
  const FILTER_PARAM = 'filter.p.tag';
  const FITMENT_TAG_BY_VARIANT = {
    'mustang-2024': 'Mustang 24',
    'mustang-2018-2023': 'Ford-Mustang-18-23',
    'mustang-2015-2017': 'Ford-Mustang-15-17',
    'mustang-2010-2014': 'Ford-Mustang-10-14',
    'mustang-2005-2009': 'Ford-Mustang-05-09',
    'wrx-2022': 'Subaru-WRX/STI-22',
    'wrx-2015-2021': 'Subaru-WRX/STI-15-21',
    'jeep-wrangler': 'Jeep',
    'bronco-2021': 'Bronco',
    'dodge-charger': 'Charger',
    'challenger-2015-2023': 'Dodge-Challenger-15-23',
    'challenger-2008-2014': 'Dodge-Challenger-08-14',
    'tesla-model-y': 'Tesla Model Y',
    'tesla-model-3': 'Tesla Model 3',
  };
  const FITMENT_TAG_ALIASES_BY_VARIANT = {
    'dodge-charger': ['Dodge-Charger-16-21', 'Dodge-Charger-16-23'],
  };

  const escapeHtml = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]);

  /* ----- shared state helpers (usable by header, chatbot, product cards) ----- */
  let memoryVehicle = null;
  let memoryOnly = false;
  const readVehicle = () => {
    if (memoryOnly) return memoryVehicle;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      memoryVehicle = raw ? JSON.parse(raw) : null;
    } catch (error) {
      /* Storage may be unavailable; retain selections made on this page. */
    }
    return memoryVehicle;
  };

  const writeVehicle = (vehicle) => {
    memoryVehicle = vehicle || null;
    try {
      if (vehicle) localStorage.setItem(STORAGE_KEY, JSON.stringify(vehicle));
      else localStorage.removeItem(STORAGE_KEY);
      memoryOnly = false;
    } catch (error) {
      memoryOnly = true;
    }
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: vehicle || null }));
  };

  // Expose a tiny read API so other scripts don't duplicate parsing.
  window.JsdVehicle = window.JsdVehicle || {};
  window.JsdVehicle.get = readVehicle;
  window.JsdVehicle.STORAGE_KEY = STORAGE_KEY;
  window.JsdVehicle.EVENT_NAME = EVENT_NAME;

  const normalizeFitmentTag = (value) => String(value || '').trim().toLowerCase()
    .replace(/[–—−]/g, '-').replace(/[\s_-]+/g, '-');

  const selectedFitmentTag = (vehicle) => {
    if (!vehicle) return '';
    return FITMENT_TAG_BY_VARIANT[vehicle.variantId] || vehicle.tag || '';
  };

  const updateFitmentCard = (card, vehicle) => {
    const badge = card.querySelector('[data-jsd-fitment-badge]');
    if (!badge) return;

    const selectedTag = normalizeFitmentTag(selectedFitmentTag(vehicle));
    if (!selectedTag) {
      badge.hidden = true;
      badge.classList.remove('is-fit', 'is-not-fit');
      card.classList.remove('has-jsd-fitment-badge');
      badge.removeAttribute('aria-label');
      return;
    }

    const productTags = (card.dataset.fitmentTags || '')
      .split('||')
      .map(normalizeFitmentTag)
      .filter(Boolean);
    const selectedTags = [selectedTag, ...(FITMENT_TAG_ALIASES_BY_VARIANT[vehicle.variantId] || []).map(normalizeFitmentTag)];
    const fits = selectedTags.some(tag => productTags.includes(tag));
    const label = badge.querySelector('[data-jsd-fitment-label]');
    const vehicleLabel = vehicle.label || [vehicle.title, vehicle.year].filter(Boolean).join(' ') || 'selected vehicle';

    badge.hidden = false;
    badge.classList.toggle('is-fit', fits);
    badge.classList.toggle('is-not-fit', !fits);
    card.classList.add('has-jsd-fitment-badge');
    if (label) label.textContent = fits ? 'Compatible' : 'Not Compatible';
    badge.setAttribute('aria-label', fits ? `Fits ${vehicleLabel}` : `Does not fit ${vehicleLabel}`);
  };

  const updateFitmentBadges = (vehicle = readVehicle(), root = document) => {
    const cards = [];
    if (root instanceof Element && root.matches('[data-fitment-card]')) cards.push(root);
    if (typeof root.querySelectorAll === 'function') cards.push(...root.querySelectorAll('[data-fitment-card]'));
    cards.forEach((card) => updateFitmentCard(card, vehicle));
  };

  const updateProductBreadcrumb = (vehicle = readVehicle()) => {
    const link = document.querySelector('[data-jsd-product-breadcrumb-collection]');
    if (!link || !vehicle?.collection) return;

    const label = vehicle.label || [vehicle.title, vehicle.year].filter(Boolean).join(' ');
    if (!label) return;

    link.href = `/collections/${encodeURIComponent(vehicle.collection)}`;
    link.textContent = label;
  };

  window.JsdVehicle.updateFitmentBadges = updateFitmentBadges;

  class JsdVehicleSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.storageKey = this.dataset.storageKey || STORAGE_KEY;
      this.families = this.parseData();

      this.panel = this.querySelector('.jsd-vs__panel');
      this.backdrop = this.querySelector('.jsd-vs__backdrop');
      this.titleEl = this.querySelector('.jsd-vs__title');
      this.subtitleEl = this.querySelector('.jsd-vs__subtitle');
      this.backBtn = this.querySelector('[data-vs-back]');
      this.grid = this.querySelector('[data-vs-grid]');
      this.viewport = this.querySelector('[data-vs-viewport]');
      this.track = this.querySelector('[data-vs-track]');
      this.scrollbar = this.querySelector('[data-vs-scrollbar]');
      this.thumb = this.querySelector('[data-vs-scrollbar-thumb]');
      this.confirm = this.querySelector('[data-vs-confirm]');
      this.confirmCard = this.querySelector('[data-vs-confirm-card]');
      this.compatibleLink = this.querySelector('[data-vs-compatible]');

      this.opener = null;
      this.activeFamily = null;

      this.bindEvents();
      this.syncHeader();
      updateFitmentBadges();
      updateProductBreadcrumb();
      this.applyCatalogFilter();
      this.ensureFilterEscape();
    }

    parseData() {
      const node = this.parentElement
        ? this.parentElement.querySelector('[data-vehicle-selector-data]')
        : document.querySelector('[data-vehicle-selector-data]');
      const source = node || document.querySelector('[data-vehicle-selector-data]');
      try {
        return JSON.parse(source.textContent).families || [];
      } catch (error) {
        return [];
      }
    }

    familyById(id) {
      return this.families.find((family) => family.id === id) || null;
    }

    bindEvents() {
      this.backdrop.addEventListener('click', () => this.close());
      this.querySelector('[data-vs-close]').addEventListener('click', () => this.close());
      this.backBtn.addEventListener('click', () => this.goBack());
      this.querySelector('[data-vs-change]').addEventListener('click', () => this.renderStep1());

      // Delegated card clicks.
      this.track.addEventListener('click', (event) => {
        const card = event.target.closest('[data-vs-card]');
        if (!card) return;
        if (card.dataset.type === 'family') this.chooseFamily(card.dataset.familyId);
        else this.chooseVariant(card.dataset.familyId, card.dataset.variantId);
      });

      this.viewport.addEventListener('scroll', () => this.updateScrollbar(), { passive: true });
      this.viewport.addEventListener('wheel', (event) => {
        // Keep native horizontal gestures and browser pinch/zoom intact.
        if (event.ctrlKey || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
        const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.viewport.clientWidth : 1;
        const previous = this.viewport.scrollLeft;
        this.scrollToPosition(previous + event.deltaY * unit);
        if (this.viewport.scrollLeft !== previous) event.preventDefault();
      }, { passive: false });
      this.viewport.addEventListener('pointerdown', (event) => this.startScrollDrag(event));
      this.viewport.addEventListener('lostpointercapture', (event) => this.finishScrollDrag(event));
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault());
      this.viewport.addEventListener('click', (event) => {
        // A completed drag must not choose the card under the release point.
        // Keyboard activation (detail === 0) remains available.
        if (!this.suppressCardClick || event.detail === 0) return;
        this.suppressCardClick = false;
        event.preventDefault();
        event.stopPropagation();
      }, true);
      if (this.scrollbar) {
        this.scrollbar.addEventListener('pointerdown', (event) => this.startScrollDrag(event, true));
        this.scrollbar.addEventListener('lostpointercapture', (event) => this.finishScrollDrag(event));
      }
      window.addEventListener('pointermove', (event) => this.moveScrollDrag(event));
      window.addEventListener('pointerup', (event) => this.finishScrollDrag(event));
      window.addEventListener('pointercancel', (event) => this.finishScrollDrag(event));
      window.addEventListener('blur', () => this.finishScrollDrag());
      window.addEventListener('resize', () => this.updateScrollbar());

      document.addEventListener('keydown', (event) => this.handleKeydown(event));
      document.addEventListener(EVENT_NAME, (event) => this.syncHeader(event.detail));

      // Any element can open the overlay.
      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-vehicle-selector-open]');
        if (!opener) return;
        event.preventDefault();
        this.open(opener);
      });
    }

    /* ---------------------------------- open / close ---------------------------------- */
    open(opener, { source = 'storefront', startOver = false } = {}) {
      this.opener = opener || null;
      this.source = source;
      const current = this.getVehicle();
      if (current && !startOver) this.renderStep3(current.familyId, current.variantId, { persist: false });
      else this.renderStep1();

      this.classList.add('is-open');
      this.panel.setAttribute('aria-hidden', 'false');
      this.panel.removeAttribute('inert');
      document.documentElement.classList.add('jsd-vs-locked');
      requestAnimationFrame(() => this.updateScrollbar());
      document.dispatchEvent(new CustomEvent('jsd:vehicle-selector-open', { detail: { source } }));
      const focusTarget = this.backBtn.hidden ? this.querySelector('[data-vs-close]') : this.backBtn;
      focusTarget.focus({ preventScroll: true });
    }

    close(reason = 'cancelled') {
      if (!this.classList.contains('is-open')) return;
      this.finishScrollDrag();
      const source = this.source;
      this.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true');
      this.panel.setAttribute('inert', '');
      document.documentElement.classList.remove('jsd-vs-locked');
      if (source !== 'chatbot' && this.opener && typeof this.opener.focus === 'function') {
        this.opener.focus({ preventScroll: true });
      }
      this.opener = null;
      this.source = null;
      document.dispatchEvent(new CustomEvent('jsd:vehicle-selector-close', {
        detail: { source, reason, vehicle: this.getVehicle() },
      }));
    }

    handleKeydown(event) {
      if (!this.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.panel.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')]
        .filter((element) => element.getClientRects().length);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    goBack() {
      // From a year-range step (step 2) back to families.
      this.renderStep1();
    }

    /* ---------------------------------- steps ---------------------------------- */
    setStep(step) {
      this.panel.dataset.step = String(step);
    }

    renderStep1() {
      this.setStep(1);
      this.activeFamily = null;
      this.titleEl.textContent = 'Select Your Vehicle';
      this.subtitleEl.textContent = 'Start your JSD Space experience by selecting your vehicle.';
      this.backBtn.hidden = true;
      this.confirm.hidden = true;
      this.grid.hidden = false;

      this.track.innerHTML = this.families.map((family) => this.cardMarkup({
        type: 'family',
        familyId: family.id,
        image: family.image,
        title: family.title,
        year: family.yearLabel,
      })).join('');
      this.resetScroll();
    }

    renderStep2(family) {
      this.setStep(2);
      this.activeFamily = family.id;
      const shortName = family.title.replace(/^\S+\s+/, '') || family.title;
      this.titleEl.textContent = `Select Your ${shortName} Year Range`;
      this.subtitleEl.textContent = family.title;
      this.backBtn.hidden = false;
      this.confirm.hidden = true;
      this.grid.hidden = false;

      this.track.innerHTML = family.variants.map((variant) => this.cardMarkup({
        type: 'variant',
        familyId: family.id,
        variantId: variant.id,
        image: variant.image,
        title: family.title,
        year: variant.year,
      })).join('');
      this.resetScroll();
    }

    renderStep3(familyId, variantId, { persist = true } = {}) {
      const family = this.familyById(familyId);
      if (!family) return this.renderStep1();
      const variant = family.variants.find((item) => item.id === variantId);
      if (!variant) return this.renderStep1();

      this.setStep(3);
      this.titleEl.textContent = "You're All Set";
      this.subtitleEl.textContent = "We'll use your vehicle to show compatible products, categories, and fitment details.";
      this.backBtn.hidden = true;
      this.grid.hidden = true;
      this.confirm.hidden = false;

      this.confirmCard.innerHTML = this.cardMarkup({
        type: 'display',
        familyId: family.id,
        variantId: variant.id,
        image: variant.image,
        title: family.title,
        year: variant.year,
      });
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      this.compatibleLink.href = `${root.replace(/\/$/, '')}/collections/${variant.collection}`;

      if (persist) {
        this.setVehicle({
          familyId: family.id,
          variantId: variant.id,
          title: family.title,
          year: variant.year,
          label: [family.title, variant.year].filter(Boolean).join(' '),
          image: variant.image,
          collection: variant.collection,
          // Raw product tag — used client-side for the future fit / no-fit badge.
          tag: variant.tag,
          // Search & Discovery filter value. Defaults to the fitment tag (the
          // store's configured filter values match the raw tags); a variant can
          // override with an explicit `filterTag` if the two ever diverge.
          filterTag: variant.filterTag || variant.tag,
        });
        if (this.source === 'chatbot') this.close('selected');
      }
    }

    chooseFamily(familyId) {
      const family = this.familyById(familyId);
      if (!family) return;
      if (family.variants.length > 1) this.renderStep2(family);
      else this.renderStep3(family.id, family.variants[0].id);
    }

    chooseVariant(familyId, variantId) {
      this.renderStep3(familyId, variantId);
    }

    cardMarkup({ type, familyId, variantId, image, title, year }) {
      const tag = type === 'display' ? 'div' : 'li';
      const inner = `
        <span class="jsd-vs__card-media">
          <img src="${escapeHtml(image)}" alt="${escapeHtml([title, year].filter(Boolean).join(' '))}" loading="lazy" width="285" height="214">
        </span>
        <span class="jsd-vs__card-body">
          <span class="jsd-vs__card-title">${escapeHtml(title)}</span>
          ${year ? `<span class="jsd-vs__card-year">${escapeHtml(year)}</span>` : ''}
        </span>`;
      if (type === 'display') return `<div class="jsd-vs__card jsd-vs__card--display">${inner}</div>`;
      return `<${tag} class="jsd-vs__card-item"><button class="jsd-vs__card" type="button" data-vs-card data-type="${type}" data-family-id="${escapeHtml(familyId)}"${variantId ? ` data-variant-id="${escapeHtml(variantId)}"` : ''}>${inner}</button></${tag}>`;
    }

    /* ---------------------------------- scrollbar ---------------------------------- */
    scrollToPosition(left) {
      const maximum = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      this.viewport.scrollLeft = Math.max(0, Math.min(maximum, left));
      this.updateScrollbar();
    }

    startScrollDrag(event, scrollbar = false) {
      // Touch on the cards keeps the browser's native swipe/momentum scrolling.
      if (event.button !== 0 || event.isPrimary === false || (!scrollbar && event.pointerType !== 'mouse')) return;
      this.finishScrollDrag();
      this.suppressCardClick = false;
      const overflow = this.viewport.scrollWidth - this.viewport.clientWidth;
      if (overflow <= 1) return;
      let scale = -1;
      const target = scrollbar ? this.scrollbar : this.viewport;
      if (scrollbar) {
        const track = this.scrollbar.getBoundingClientRect();
        const thumb = this.thumb.getBoundingClientRect();
        const travel = track.width - thumb.width;
        if (travel <= 0) return;
        scale = overflow / travel;
        event.preventDefault();
        if (event.target !== this.thumb) {
          this.scrollToPosition((event.clientX - track.left - thumb.width / 2) * scale);
        }
      }
      this.scrollDrag = {
        pointerId: event.pointerId, startX: event.clientX,
        startScroll: this.viewport.scrollLeft, scale, target, scrollbar, moved: false,
      };
      if (scrollbar) target.setPointerCapture(event.pointerId);
    }

    moveScrollDrag(event) {
      const drag = this.scrollDrag;
      if (!drag || event.pointerId !== drag.pointerId) return;
      const distance = event.clientX - drag.startX;
      if (!drag.moved && Math.abs(distance) < 6) return;
      if (!drag.moved) {
        drag.moved = true;
        drag.target.setPointerCapture(drag.pointerId);
        this.viewport.classList.add('is-dragging');
      }
      if (!drag.scrollbar) this.suppressCardClick = true;
      event.preventDefault();
      this.scrollToPosition(drag.startScroll + distance * drag.scale);
    }

    finishScrollDrag(event) {
      const drag = this.scrollDrag;
      if (!drag || (event && event.pointerId !== drag.pointerId)) return;
      this.scrollDrag = null;
      this.viewport.classList.remove('is-dragging');
      if (drag.target.hasPointerCapture(drag.pointerId)) drag.target.releasePointerCapture(drag.pointerId);
    }

    resetScroll() {
      this.finishScrollDrag();
      this.viewport.scrollLeft = 0;
      requestAnimationFrame(() => this.updateScrollbar());
    }

    updateScrollbar() {
      if (!this.scrollbar || this.grid.hidden) return;
      const { scrollWidth, clientWidth, scrollLeft } = this.viewport;
      const overflow = scrollWidth - clientWidth;
      if (overflow <= 1) {
        this.scrollbar.hidden = true;
        return;
      }
      this.scrollbar.hidden = false;
      const trackWidth = this.scrollbar.clientWidth;
      const thumbWidth = Math.max(40, (clientWidth / scrollWidth) * trackWidth);
      const maxOffset = trackWidth - thumbWidth;
      const offset = (scrollLeft / overflow) * maxOffset;
      this.thumb.style.width = `${thumbWidth}px`;
      this.thumb.style.transform = `translateX(${offset}px)`;
    }

    /* ---------------------------------- persistence + sync ---------------------------------- */
    getVehicle() {
      return readVehicle();
    }

    setVehicle(vehicle) {
      writeVehicle(vehicle);
      this.syncHeader(vehicle);
    }

    syncHeader(vehicle = readVehicle()) {
      const pills = document.querySelectorAll('[data-vehicle-pill]');
      pills.forEach((pill) => {
        pill.classList.toggle('is-selected', Boolean(vehicle));
        const emptyEl = pill.querySelector('[data-vehicle-empty]');
        const selectedEl = pill.querySelector('[data-vehicle-selected]');
        if (emptyEl) emptyEl.hidden = Boolean(vehicle);
        if (selectedEl) selectedEl.hidden = !vehicle;
        if (vehicle) {
          const nameEl = pill.querySelector('[data-vehicle-name]');
          const thumbEl = pill.querySelector('[data-vehicle-thumb]');
          if (nameEl) {
            const model = vehicle.title || vehicle.model || vehicle.label || '';
            const year = vehicle.year || '';
            nameEl.innerHTML = `
              <span class="header__vehicle-model">${escapeHtml(model)}</span>
              ${year ? `<span class="header__vehicle-year">${escapeHtml(year)}</span>` : ''}
            `;
          }
          if (thumbEl) thumbEl.src = vehicle.image;
        }
      });
    }

    /* ---------------------------------- catalog auto-filter ---------------------------------- */
    // Attach the selected vehicle's Search & Discovery filter value to search
    // submissions so results are fitment-filtered (removable by the shopper on
    // the results page via the S&D filter chip). Narrow queries may legitimately
    // return few/no results when nothing compatible matches the search term.
    applyCatalogFilter() {
      const forms = document.querySelectorAll('form[action$="/search"], form[action*="/search?"]');
      forms.forEach((form) => {
        if (form.dataset.jsdVehicleFilterBound === 'true') return;
        form.dataset.jsdVehicleFilterBound = 'true';
        form.addEventListener('submit', () => {
          form.querySelectorAll(`input[name="${FILTER_PARAM}"][data-jsd-vehicle-filter]`).forEach((input) => input.remove());
          const vehicle = readVehicle();
          if (!vehicle || !vehicle.filterTag) return;
          // Always (re)apply on a new search: the search-query forms carry only
          // `q`, so a fresh submit would otherwise DROP an active vehicle filter.
          // Skip only if the form itself already carries this exact filter value
          // (e.g. a facet form), to avoid a duplicate param.
          const existing = Array.from(form.querySelectorAll(`input[name="${FILTER_PARAM}"]`))
            .some((input) => input.value === vehicle.filterTag);
          if (existing) return;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = FILTER_PARAM;
          input.value = vehicle.filterTag;
          input.dataset.jsdVehicleFilter = 'true';
          form.appendChild(input);
        });
      });
    }

    // When our filter is applied but the search returns 0 results, Shopify
    // returns no facets, so the theme renders neither the results-sidebar nor a
    // removable filter chip — the shopper gets stuck. Inject a "Show all
    // results" escape (only when Shopify itself offers no removal control).
    ensureFilterEscape() {
      const vehicle = readVehicle();
      if (!vehicle || !vehicle.filterTag) return;
      const params = new URLSearchParams(window.location.search);
      if (!params.getAll(FILTER_PARAM).includes(vehicle.filterTag)) return;
      if (document.querySelector('.active-facets')) return; // Shopify already shows a removable chip

      const searchBar = document.querySelector('.template-search__search');
      const fallbackHost = document.querySelector('#ProductGridContainer, .template-search__results, main');
      if ((!searchBar && !fallbackHost) || document.querySelector('[data-jsd-filter-escape]')) return;

      const url = new URL(window.location.href);
      const kept = url.searchParams.getAll(FILTER_PARAM).filter((value) => value !== vehicle.filterTag);
      url.searchParams.delete(FILTER_PARAM);
      kept.forEach((value) => url.searchParams.append(FILTER_PARAM, value));

      const banner = document.createElement('div');
      banner.className = 'jsd-filter-escape';
      banner.dataset.jsdFilterEscape = 'true';
      banner.innerHTML = `<span>Filtered to <strong>${escapeHtml(vehicle.label)}</strong>.</span>`
        + `<a href="${escapeHtml(url.pathname + url.search)}">Show all results</a>`;
      if (searchBar) {
        searchBar.insertAdjacentElement('afterend', banner);
      } else {
        fallbackHost.prepend(banner);
      }
    }
  }

  if (!customElements.get('jsd-vehicle-selector')) {
    customElements.define('jsd-vehicle-selector', JsdVehicleSelector);
  }

  document.addEventListener(EVENT_NAME, (event) => {
    updateFitmentBadges(event.detail);
    updateProductBreadcrumb(event.detail);
  });

  const fitmentCardObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) updateFitmentBadges(readVehicle(), node);
      });
    });
  });
  fitmentCardObserver.observe(document.documentElement, { childList: true, subtree: true });

  // Keep header pills in sync even when the change originates in another tab
  // or from the chatbot writing the same key.
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY && event.key !== null) return;
    memoryOnly = false;
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: readVehicle() }));
  });
})();
